import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { LGBMRParamsProps } from './types';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

const inputBase = 'w-full border rounded-lg px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-foreground bg-card disabled:bg-muted disabled:text-muted-foreground transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring border-input [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

export const LGBMRParams: React.FC<LGBMRParamsProps> = memo(({
  modelParams,
  disabled,
  onParameterChange
}) => {
  const { t } = useTranslation('training');

  return (
    <>
      {/* N_ESTIMATORS */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-center gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1">
          <label
            htmlFor="model-config-N_ESTIMATORS"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.nEstimators')}
          </label>
          <InfoTooltip text={t('tooltips.models.lgbmr.nEstimators')} width="w-72" />
        </div>
        <input
          id="model-config-N_ESTIMATORS"
          type="number"
          value={modelParams.N_ESTIMATORS ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            onParameterChange('N_ESTIMATORS', val ? parseInt(val, 10) : undefined);
          }}
          disabled={disabled}
          className={inputBase}
          min="1"
          max="10000"
          step="1"
          placeholder={t('training.modelConfiguration.placeholders.enterNumber')}
        />
      </div>

      {/* LEARNING_RATE */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-center gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1">
          <label
            htmlFor="model-config-LEARNING_RATE"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.learningRate')}
          </label>
          <InfoTooltip text={t('tooltips.models.lgbmr.learningRate')} width="w-72" />
        </div>
        <input
          id="model-config-LEARNING_RATE"
          type="number"
          value={modelParams.LEARNING_RATE ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            onParameterChange('LEARNING_RATE', val ? parseFloat(val) : undefined);
          }}
          disabled={disabled}
          className={inputBase}
          min="0.001"
          max="1"
          step="0.01"
          placeholder={t('training.modelConfiguration.placeholders.enterValue')}
        />
      </div>

      {/* MAX_DEPTH */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-center gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1">
          <label
            htmlFor="model-config-MAX_DEPTH"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.maxDepth')}
          </label>
          <InfoTooltip text={t('tooltips.models.lgbmr.maxDepth')} width="w-72" />
        </div>
        <input
          id="model-config-MAX_DEPTH"
          type="number"
          value={modelParams.MAX_DEPTH ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            onParameterChange('MAX_DEPTH', val ? parseInt(val, 10) : undefined);
          }}
          disabled={disabled}
          className={inputBase}
          min="-1"
          max="100"
          step="1"
          placeholder={t('training.modelConfiguration.placeholders.enterValue')}
        />
      </div>
    </>
  );
});

LGBMRParams.displayName = 'LGBMRParams';

export default LGBMRParams;
